import { create } from 'zustand';
import type { StorageValue } from 'zustand/middleware';
import { persist } from 'zustand/middleware';
import { hiddenPaymentMethodStoreKey } from '../constants/storage-keys';

interface HiddenPaymentMethodsState {
  // All hidden payment-method IDs
  hiddenIds: Set<string>;

  // Add one or more IDs to the set
  addHiddenIds: (ids: string[]) => void;

  // Remove one or more IDs from the set
  removeHiddenIds: (ids: string[]) => void;
}

export const useHiddenPaymentMethodsStore = create<HiddenPaymentMethodsState>()(
  persist(
    (set, _get) => ({
      addHiddenIds: (ids: string[]) => {
        set((state) => {
          const newSet = new Set(state.hiddenIds);
          let changed = false;

          for (const id of ids) {
            if (!newSet.has(id)) {
              newSet.add(id);
              changed = true;
            }
          }

          // Skip the update
          if (!changed) {
            return {};
          }

          return { hiddenIds: newSet };
        });
      },

      hiddenIds: new Set<string>(),

      removeHiddenIds: (ids: string[]) => {
        set((state) => {
          const newSet = new Set(state.hiddenIds);
          let changed = false;

          for (const id of ids) {
            if (newSet.has(id)) {
              newSet.delete(id);
              changed = true;
            }
          }

          // Skip the update
          if (!changed) {
            return {};
          }

          return { hiddenIds: newSet };
        });
      },
    }),
    {
      name: hiddenPaymentMethodStoreKey,
      // Since we use a Set<string>: https://zustand.docs.pmnd.rs/integrations/persisting-store-data#how-do-i-use-it-with-map-and-set
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const existingValue = JSON.parse(str);
          return {
            ...existingValue,
            state: {
              ...existingValue.state,
              hiddenIds: new Set(existingValue.state.hiddenIds),
            },
          };
        },
        removeItem: (name) => localStorage.removeItem(name),
        setItem: (name, newValue: StorageValue<HiddenPaymentMethodsState>) => {
          const str = JSON.stringify({
            ...newValue,
            state: {
              ...newValue.state,
              hiddenIds: Array.from(newValue.state.hiddenIds),
            },
          });
          localStorage.setItem(name, str);
        },
      },
    }
  )
);

export const useHiddenPaymentMethods = () => useHiddenPaymentMethodsStore((state) => state.hiddenIds);
export const useAddHiddenIds = () => useHiddenPaymentMethodsStore((state) => state.addHiddenIds);
export const useRemoveHiddenIds = () => useHiddenPaymentMethodsStore((state) => state.removeHiddenIds);
